import type { RoadSenseMovementSeries, RoadSenseTrend } from "@visioni/a3s-reports/client-messages";

/**
 * Client-side traffic filters for the RoadSense report.
 *
 * The payload ships an **hourly** series (see `RoadSenseTrend`); these filters
 * do all of their work client-side — slicing the date window, picking movements
 * and vehicle classes, and bucketing intervals into coarser granularities (hour
 * up to year) — so no backend round trip is needed to re-scope the chart. (The
 * edge `TrafficFlowBucket` cadence is finer — 15 minutes — but we deliberately
 * roll up to hourly for the report: sub-hour detail isn't useful for customers
 * and bloats the wire payload.) This matches how
 * the example exports behave (they aggregate a base series in the browser) and
 * keeps the wire contract small.
 *
 * Two dimensions are **multi-select**, with deliberately different chart
 * semantics:
 *
 * - **Classification** (car/truck/bus) is *summed*: picking Car + Truck draws a
 *   single line of their combined count. It answers "how much of this traffic is
 *   X?", so overlaying separate class lines would just clutter.
 * - **Movement** is drawn as *distinct lines*: picking Northbound + Southbound
 *   draws one line each, because comparing flows against each other is the
 *   whole point of the movement breakdown.
 *
 * For both, an **empty selection means "All"** — the UI surfaces this as an
 * "All" chip that clears the specific picks (and picking a specific value
 * clears "All"). So the state is always either All or an explicit subset.
 *
 * "Movement" is the neutral term for the dimension the reference exports label
 * "Direction": a travel direction (Northbound/Southbound) on a simple road, but
 * a discrete counting point (Point 1, Western approach) on a multi-point site.
 * Chips are driven by `trend.movements`, so a single-movement site shows no
 * movement filter at all.
 */

/** Time bucket the base hourly series is rolled up into for display. */
export type TrafficGranularity = "hour" | "day" | "week" | "month" | "year";

/** A concrete vehicle class (the multi-select values; "All" == empty set). */
export type TrafficClass = "car" | "truck" | "bus";

export interface TrafficFilters {
  granularity: TrafficGranularity;
  /** Selected vehicle classes; **empty means "All"** (combined total). Summed. */
  classifications: TrafficClass[];
  /** Selected movement ids; **empty means "All"**. Drawn as distinct lines. */
  movements: string[];
  /** Inclusive ISO (`YYYY-MM-DD`) lower bound, or `null` for "from the start". */
  fromDate: string | null;
  /** Inclusive ISO (`YYYY-MM-DD`) upper bound, or `null` for "to the end". */
  toDate: string | null;
}

export const DEFAULT_TRAFFIC_FILTERS: TrafficFilters = {
  granularity: "day",
  classifications: [],
  movements: [],
  fromDate: null,
  toDate: null,
};

/**
 * Thresholds (in days of *visible* span) at which the **default** granularity
 * steps up to a coarser bucket, so any window — a two-day zoom or a two-year
 * hire — opens on a chart with a readable number of points (≈30–340) rather
 * than thousands of hourly samples or a handful of yearly bars.
 *
 * Tuned against the real cadence (24 hourly points per day): a window under a
 * week reads best hourly (<168 pts), then day / week / month as the span grows.
 * "Year" is never auto-selected — the span where it reads best is larger than
 * our hires realistically produce, so we leave it to the user. Ordered
 * coarsest-first; the first threshold the span clears wins, and anything below
 * the finest threshold falls back to the finest granularity (hour).
 *
 * The hour→day step (6, i.e. hour defaults only for windows under 7 days) sits
 * well below the hour disable cap (`GRANULARITY_WINDOW_LIMIT.hour` = 14), so a
 * freshly opened report never defaults to hour only to have it immediately
 * clamped to day at the boundary.
 */
const GRANULARITY_DEFAULT_THRESHOLDS: readonly { minDays: number; granularity: TrafficGranularity }[] = [
  { minDays: 240, granularity: "month" },
  { minDays: 45, granularity: "week" },
  { minDays: 6, granularity: "day" },
];

/**
 * Pick the default granularity for a window spanning `days`: hour under 7 days,
 * day to 45, week to 240, month beyond. Never returns "year" (see thresholds
 * above).
 */
export function defaultGranularityForDays(days: number): TrafficGranularity {
  return GRANULARITY_DEFAULT_THRESHOLDS.find((t) => days > t.minDays)?.granularity ?? "hour";
}

/**
 * Span (in whole-ish days) covered by an `instants` array — `(last − first)` in
 * ms, as days. Drives the default-granularity heuristic, which cares about the
 * window's *duration*, not how many hourly samples it holds (otherwise a dense
 * base series would always look "huge"). Returns 0 for an empty/one-point series
 * so it falls to the finest default.
 */
export function spanDays(instants: readonly string[]): number {
  if (instants.length < 2) return 0;
  const first = Date.parse(instants[0] ?? "");
  const last = Date.parse(instants[instants.length - 1] ?? "");
  if (Number.isNaN(first) || Number.isNaN(last)) return 0;
  return (last - first) / 86_400_000;
}

/**
 * The starting filter state for a window spanning `days` — defaults with the
 * data-aware granularity baked in. Pass the trend's span (see {@link spanDays})
 * so a freshly opened report (and the "Reset filters" action) lands on a
 * sensible bucket.
 */
export function defaultTrafficFilters(days: number): TrafficFilters {
  return { ...DEFAULT_TRAFFIC_FILTERS, granularity: defaultGranularityForDays(days) };
}

/**
 * Whether `filters` are at their defaults for a window spanning `days`.
 * Granularity is compared against the **data-aware** default so a report that
 * opens on (say) "week" still reads as pristine and keeps a clean URL. `days`
 * defaults to a tiny window (→ finest default) for callers that don't have the
 * span yet.
 */
export function isDefaultFilters(filters: TrafficFilters, days = 0): boolean {
  return (
    filters.granularity === defaultGranularityForDays(days) &&
    filters.classifications.length === 0 &&
    filters.movements.length === 0 &&
    filters.fromDate === DEFAULT_TRAFFIC_FILTERS.fromDate &&
    filters.toDate === DEFAULT_TRAFFIC_FILTERS.toDate
  );
}

export const GRANULARITY_OPTIONS: readonly { value: TrafficGranularity; label: string }[] = [
  { value: "hour", label: "Hour" },
  { value: "day", label: "Day" },
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
  { value: "year", label: "Year" },
];

/** Every granularity value, for parsing/validating URL params. */
export const TRAFFIC_GRANULARITIES: readonly TrafficGranularity[] = GRANULARITY_OPTIONS.map((o) => o.value);

/**
 * Human-readable form of the default-granularity rule, one row per bucket
 * (finest → coarsest), for the help popover. Derived from
 * {@link GRANULARITY_DEFAULT_THRESHOLDS} so the explanation can't drift from the
 * logic. "Year" is omitted: it's never an automatic default, only user-picked.
 */
export const GRANULARITY_DEFAULT_RULES: readonly { label: string; range: string }[] = (() => {
  const ascending = [...GRANULARITY_DEFAULT_THRESHOLDS].reverse();
  const labelFor = (g: TrafficGranularity): string => GRANULARITY_OPTIONS.find((o) => o.value === g)?.label ?? g;

  const finest = ascending[0];
  if (finest === undefined) return [];

  // The fallback (finest) granularity covers everything below the first step.
  const rows: { label: string; range: string }[] = [{ label: "Hour", range: `under ${finest.minDays + 1} days` }];
  ascending.forEach((step, i) => {
    const next = ascending[i + 1];
    rows.push({
      label: labelFor(step.granularity),
      range: next ? `${step.minDays + 1}–${next.minDays} days` : `over ${step.minDays} days`,
    });
  });
  return rows;
})();

/**
 * Largest selected window at which a sub-day granularity is still offered. Past
 * this the bucket count is too high to read or render usefully (and, for a
 * backend-fetched series, too large to ship), so the chip is disabled and an
 * explanation is surfaced instead of silently doing nothing.
 *
 * `maxDays` is the *maximum still-allowed* span — a window strictly greater than
 * it disables the granularity. Hourly caps at 14 days (≈336 points); beyond that
 * the points crowd illegibly on the small chart (300+ on a narrow plot). `label`
 * is the human phrase the popover uses, so the message reads naturally.
 */
const GRANULARITY_WINDOW_LIMIT: Partial<Record<TrafficGranularity, { maxDays: number; label: string }>> = {
  hour: { maxDays: 14, label: "14 days" },
};

/** Granularity a disabled sub-day selection falls back to when the window grows. */
const GRANULARITY_DOWNGRADE_TARGET: TrafficGranularity = "day";

/**
 * Why `granularity` is unavailable for a window spanning `windowDays`, or `null`
 * if it's allowed. Drives both the disabled chip state and its explanatory
 * popover, so the rule and its wording live in one place. Only the sub-day
 * granularities are ever capped (see {@link GRANULARITY_WINDOW_LIMIT}).
 */
export function granularityDisabledReason(granularity: TrafficGranularity, windowDays: number): string | null {
  const limit = GRANULARITY_WINDOW_LIMIT[granularity];
  if (limit === undefined || windowDays <= limit.maxDays) return null;
  const label = GRANULARITY_OPTIONS.find((o) => o.value === granularity)?.label ?? granularity;
  return `${label} buckets are only available for a date range of ${limit.label} or less. Narrow the date range to use this.`;
}

/** Whether `granularity` is selectable for a window spanning `windowDays`. */
export function isGranularityAvailable(granularity: TrafficGranularity, windowDays: number): boolean {
  return granularityDisabledReason(granularity, windowDays) === null;
}

/**
 * If `filters.granularity` is no longer available for `windowDays` (the user
 * widened the date range past a sub-day cap), downgrade it to
 * {@link GRANULARITY_DOWNGRADE_TARGET} so the chart never sits on a granularity
 * it can't render; otherwise return `filters` unchanged (same reference, so it's
 * safe to call on every render / window change). Callers apply this whenever the
 * window changes.
 */
export function clampGranularityToWindow(filters: TrafficFilters, windowDays: number): TrafficFilters {
  if (isGranularityAvailable(filters.granularity, windowDays)) return filters;
  return { ...filters, granularity: GRANULARITY_DOWNGRADE_TARGET };
}

/**
 * Resolve the **selected** window to a day count: the gap between the chosen
 * `fromDate`/`toDate`, each falling back to the data edge (`dataMin`/`dataMax`,
 * the trend's first/last day) when unset. This is what the granularity caps and
 * the downgrade test measure — what the user is actually looking at, not the
 * full dataset. Inclusive of both ends (a single-day window is 1 day).
 */
export function effectiveWindowDays(filters: TrafficFilters, dataMin: string, dataMax: string): number {
  const from = filters.fromDate ?? dataMin;
  const to = filters.toDate ?? dataMax;
  const fromMs = Date.parse(`${from}T00:00:00Z`);
  const toMs = Date.parse(`${to}T00:00:00Z`);
  if (Number.isNaN(fromMs) || Number.isNaN(toMs)) return 0;
  return Math.max(0, (toMs - fromMs) / 86_400_000) + 1;
}

/**
 * Site-local calendar-date (`YYYY-MM-DD`) bounds of an ascending `instants`
 * series — the first/last day the data covers. `null` for an
 * empty/unparseable series. Shared by the date pickers and the
 * granularity-window clamp so both measure the window against the same edges.
 */
export function instantsDayBounds(instants: readonly string[]): { min: string; max: string } | null {
  if (instants.length === 0) return null;
  const min = dayKeyOf(instants[0]);
  const max = dayKeyOf(instants[instants.length - 1]);
  if (min === null || max === null) return null;
  return { min, max };
}

/** Site-local `YYYY-MM-DD` portion of an offset ISO instant, or `null`. */
function dayKeyOf(iso: string | undefined): string | null {
  return siteLocalDate(iso)?.dayKey ?? null;
}

/**
 * Turn a day key (`YYYY-MM-DD`, as produced by {@link instantsDayBounds}) into
 * the `Date` the date pickers take for their min/max bounds, or `undefined` if
 * it is unreadable.
 *
 * Anchored at **UTC midday**, matching the instant the pickers emit on change.
 * The web picker serialises a bound with `toISOString()` and keeps the leading
 * date, so a browser-local midnight would round-trip to the wrong day for any
 * viewer east of UTC: a Sydney viewer read 31 Aug back as 30 Aug and so could
 * not select the last day of data. Midday survives every offset in +/-12.
 */
export function dayKeyToDate(dayKey: string): Date | undefined {
  const [year, month, day] = dayKey.split("-").map(Number);
  if (!year || !month || !day) return undefined;
  return new Date(Date.UTC(year, month - 1, day, 12));
}

/** Concrete-class chips (the "All" chip is rendered separately by the UI). */
export const CLASS_OPTIONS: readonly { value: TrafficClass; label: string }[] = [
  { value: "car", label: "Car" },
  { value: "truck", label: "Truck" },
  { value: "bus", label: "Bus" },
];

/**
 * The result of applying filters: the series the chart should render plus
 * whether weekend shading still applies. Shading only makes sense once a point
 * maps to a single day or finer (hour / day) — a week/month/year bucket spans
 * weekends, so a single shaded point would be meaningless.
 *
 * When the payload has movements, the chart draws one line per movement
 * (carried in `trend.movements`) — including the "All" case, which keeps every
 * movement. The single combined `total` line is only used for payloads with no
 * movements at all. Classification has already been folded into every series
 * here, so the chart never has to know about classes.
 */
export interface FilteredTrend {
  trend: RoadSenseTrend;
  showWeekendShading: boolean;
}

/** Granularities at or finer than a day, where weekend shading is meaningful. */
const SUB_DAY_OR_DAY: ReadonlySet<TrafficGranularity> = new Set<TrafficGranularity>(["hour", "day"]);

/**
 * Apply `filters` to the base `trend`, producing the series the chart should
 * render. Order: (1) restrict to the date window, (2) pick the movements to
 * draw, (3) fold the selected classes into every series (sum), (4) bucket into
 * the chosen granularity.
 *
 * Pure over the payload — never mutates `trend`, so the original hourly series
 * stays available for re-filtering as the user changes controls.
 */
export function applyTrafficFilters(trend: RoadSenseTrend, filters: TrafficFilters): FilteredTrend {
  const windowed = sliceToDateWindow(trend, filters.fromDate, filters.toDate);
  const moved = selectMovements(windowed, filters.movements);
  const classed = applyClassification(moved, filters.classifications);
  const bucketed = aggregateByGranularity(classed, filters.granularity);
  return { trend: bucketed, showWeekendShading: SUB_DAY_OR_DAY.has(filters.granularity) };
}

/** Movement chip values derived from the payload (no "All" — UI adds that). */
export function movementOptions(trend: RoadSenseTrend): readonly { value: string; label: string }[] {
  return (trend.movements ?? []).map((m) => ({ value: m.id, label: m.label }));
}

/**
 * Keep only the selected movements; **empty means "All" → keep every movement**
 * so the chart draws one line per movement (Northbound, Southbound, …) plus its
 * dashed "All" sum line. A non-empty selection narrows to those movements; a
 * stale/garbage selection that matches nothing falls back to all movements
 * rather than rendering an empty chart.
 *
 * (The single combined `total` line is only used when the payload carries no
 * movements at all — `movementOptions` then returns nothing and the Movement
 * filter is hidden.)
 */
function selectMovements(trend: RoadSenseTrend, selected: readonly string[]): RoadSenseTrend {
  const all = trend.movements ?? [];
  if (all.length === 0) return trend;
  if (selected.length === 0) return trend;
  const kept = all.filter((m) => selected.includes(m.id));
  if (kept.length === 0) return trend;
  return { ...trend, movements: kept };
}

/**
 * Fold the selected classes into the combined `total_series` and into every
 * movement's `total_series` (sum of the chosen class series). Empty selection
 * is a passthrough ("All" = the pre-computed combined total). After this the
 * per-class series are dropped — classification is expressed purely through the
 * totals the chart plots.
 */
function applyClassification(trend: RoadSenseTrend, classes: readonly TrafficClass[]): RoadSenseTrend {
  if (classes.length === 0) return trend;
  return {
    labels: trend.labels,
    instants: trend.instants,
    total_series: sumClasses(trend, classes),
    movements: trend.movements?.map((m) => ({
      id: m.id,
      label: m.label,
      total_series: sumClasses(m, classes),
    })),
  };
}

/**
 * Sum the chosen class series of a series-carrier (the trend or a movement)
 * interval-by-interval. Falls back to that carrier's `total_series` for any
 * class it doesn't break down, so an unclassified payload still charts something
 * rather than zeroing out.
 */
function sumClasses(
  carrier: Pick<RoadSenseMovementSeries, "total_series" | "car_series" | "truck_series" | "bus_series">,
  classes: readonly TrafficClass[],
): number[] {
  const seriesFor = (c: TrafficClass): readonly number[] | undefined =>
    c === "car" ? carrier.car_series
    : c === "truck" ? carrier.truck_series
    : carrier.bus_series;

  const picked = classes.map(seriesFor);
  // If none of the selected classes are broken out, fall back to the total.
  if (picked.every((s) => s === undefined)) return [...carrier.total_series];

  const n = carrier.total_series.length;
  const out = new Array<number>(n).fill(0);
  for (const series of picked) {
    if (series === undefined) continue;
    for (let i = 0; i < n; i += 1) out[i] = (out[i] ?? 0) + (series[i] ?? 0);
  }
  return out;
}

/**
 * Restrict every parallel series (and each movement) to the `[from, to]` date
 * window. Bounds are calendar dates (`YYYY-MM-DD`) compared against each
 * instant's date portion, so `to` is **inclusive of the whole day** — picking
 * "to 2026-06-10" keeps every hourly interval up to 23:00 that day.
 */
function sliceToDateWindow(trend: RoadSenseTrend, fromDate: string | null, toDate: string | null): RoadSenseTrend {
  if (fromDate === null && toDate === null) return trend;
  const keep: number[] = [];
  for (let i = 0; i < trend.instants.length; i += 1) {
    const day = dayOf(trend.instants[i]);
    if (day === null) continue;
    if (fromDate !== null && day < fromDate) continue;
    if (toDate !== null && day > toDate) continue;
    keep.push(i);
  }
  return pickIndices(trend, keep);
}

/**
 * Roll base intervals up into hour / day / week / month / year buckets, summing
 * each series (including every movement). Bucket keys come from the instant; a
 * representative instant (the bucket's first interval) is kept so downstream
 * code still has a time per point. Hour is effectively a passthrough at the
 * hourly base, but is handled uniformly so a finer-than-base future cadence
 * still collapses correctly.
 */
function aggregateByGranularity(trend: RoadSenseTrend, granularity: TrafficGranularity): RoadSenseTrend {
  const buckets = new Map<string, number[]>();
  const order: string[] = [];
  const bucketFirstInstant = new Map<string, string>();

  for (let i = 0; i < trend.instants.length; i += 1) {
    const iso = trend.instants[i];
    if (iso === undefined) continue;
    const key = bucketKey(iso, granularity);
    if (!buckets.has(key)) {
      buckets.set(key, []);
      order.push(key);
      bucketFirstInstant.set(key, iso);
    }
    buckets.get(key)!.push(i);
  }

  const sumOver = (series: readonly number[], indices: number[]): number => {
    let sum = 0;
    for (const idx of indices) sum += series[idx] ?? 0;
    return sum;
  };

  const labels: string[] = [];
  const instants: string[] = [];
  const total: number[] = [];
  const movementTotals = (trend.movements ?? []).map(() => [] as number[]);

  for (const key of order) {
    const indices = buckets.get(key)!;
    const firstIso = bucketFirstInstant.get(key)!;
    labels.push(bucketLabel(firstIso, granularity));
    instants.push(firstIso);
    total.push(sumOver(trend.total_series, indices));
    (trend.movements ?? []).forEach((m, mi) => {
      movementTotals[mi]!.push(sumOver(m.total_series, indices));
    });
  }

  return {
    labels,
    instants: instants as RoadSenseTrend["instants"],
    total_series: total,
    movements: trend.movements?.map((m, mi) => ({
      id: m.id,
      label: m.label,
      total_series: movementTotals[mi]!,
    })),
  };
}

/** Stable, sortable-by-insertion bucket key for the chosen granularity. */
function bucketKey(iso: string, granularity: TrafficGranularity): string {
  const local = siteLocalDate(iso);
  if (local === null) return iso;
  const year = local.date.getUTCFullYear();
  if (granularity === "year") return String(year);
  if (granularity === "month") return local.dayKey.slice(0, 7);
  if (granularity === "week") {
    // Key by ISO year-week so weeks straddling a month/year stay together.
    const { isoYear, isoWeek } = isoYearWeek(local.date);
    return `${isoYear}-W${pad(isoWeek)}`;
  }
  if (granularity === "day") return local.dayKey;
  // Hour (the finest granularity): day plus the clock hour.
  return `${local.dayKey}T${pad(local.date.getUTCHours())}`;
}

/** Human axis label for a bucket, from its first instant. */
function bucketLabel(firstIso: string, granularity: TrafficGranularity): string {
  const local = siteLocalDate(firstIso);
  if (local === null) return firstIso;
  const date = local.date;
  if (granularity === "year") return String(date.getUTCFullYear());
  if (granularity === "month") {
    return date.toLocaleDateString("en-AU", { month: "short", year: "numeric", timeZone: "UTC" });
  }
  if (granularity === "week") {
    // Week buckets show only the commencing date — the granularity is already
    // stated in the filter controls and summary, so a per-tick "w/c " prefix is
    // just noise that crowds the axis.
    return date.toLocaleDateString("en-AU", { day: "numeric", month: "short", timeZone: "UTC" });
  }
  if (granularity === "day") {
    return date.toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short", timeZone: "UTC" });
  }
  // Hour: a day-of-month plus the clock time, so a long intraday window still
  // says which day a point belongs to.
  const day = date.toLocaleDateString("en-AU", { day: "numeric", month: "short", timeZone: "UTC" });
  const time = date.toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "UTC" });
  return `${day} ${time}`;
}

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

/**
 * ISO-8601 week number (weeks start Monday; week 1 contains the first Thursday)
 * for a date. Used so weekly buckets are stable across year boundaries.
 */
function isoYearWeek(input: Date): { isoYear: number; isoWeek: number } {
  const date = new Date(Date.UTC(input.getUTCFullYear(), input.getUTCMonth(), input.getUTCDate(), 12));
  const day = date.getUTCDay() === 0 ? 7 : date.getUTCDay();
  date.setUTCDate(date.getUTCDate() + 4 - day);
  const isoYear = date.getUTCFullYear();
  const yearStart = new Date(Date.UTC(isoYear, 0, 1));
  const isoWeek = Math.ceil(((date.getTime() - yearStart.getTime()) / 86_400_000 + 1) / 7);
  return { isoYear, isoWeek };
}

/**
 * Read an offset ISO instant as the site's wall-clock date/time.
 *
 * `RoadSenseTrend.instants` deliberately carries the camera-local offset. A
 * normal `new Date(iso)` converts that value to UTC, which changes the calendar
 * day for Sydney's early-morning hours. Appending `Z` to the wall-clock prefix
 * gives us a `Date` whose UTC getters preserve those local components while
 * remaining independent of the viewer's browser timezone.
 */
function siteLocalDate(iso: string | undefined): { date: Date; dayKey: string } | null {
  if (iso === undefined) return null;
  const match = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2}):(\d{2})/.exec(iso);
  if (match === null) return null;
  const dayKey = match[1];
  const hour = match[2];
  const minute = match[3];
  const second = match[4];
  if (dayKey === undefined || hour === undefined || minute === undefined || second === undefined) return null;
  const date = new Date(`${dayKey}T${hour}:${minute}:${second}Z`);
  if (Number.isNaN(date.getTime())) return null;
  return { date, dayKey };
}

/** Whether an offset ISO instant falls on Saturday/Sunday at the site. */
export function isSiteLocalWeekend(iso: string | undefined): boolean {
  const local = siteLocalDate(iso);
  if (local === null) return false;
  const day = local.date.getUTCDay();
  return day === 0 || day === 6;
}

/** The site-local `YYYY-MM-DD` portion of an ISO instant, or `null`. */
function dayOf(iso: string | undefined): string | null {
  return siteLocalDate(iso)?.dayKey ?? null;
}

/** Select the same `indices` (in order) from the trend and every movement. */
function pickIndices(trend: RoadSenseTrend, indices: number[]): RoadSenseTrend {
  const pick = (series: readonly number[]): number[] => indices.map((i) => series[i] ?? 0);
  const pickOpt = (series: readonly number[] | undefined): number[] | undefined =>
    series === undefined ? undefined : pick(series);
  return {
    labels: indices.map((i) => trend.labels[i] ?? ""),
    instants: indices.map((i) => trend.instants[i] ?? "") as RoadSenseTrend["instants"],
    total_series: pick(trend.total_series),
    car_series: pickOpt(trend.car_series),
    truck_series: pickOpt(trend.truck_series),
    bus_series: pickOpt(trend.bus_series),
    movements: trend.movements?.map((m) => ({
      id: m.id,
      label: m.label,
      total_series: pick(m.total_series),
      car_series: pickOpt(m.car_series),
      truck_series: pickOpt(m.truck_series),
      bus_series: pickOpt(m.bus_series),
    })),
  };
}
